/**
 * Created by Alex Poh. on 23/03/20.
 * Copyright © 2020 Curriculum Ltd. All rights reserved.
 */
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { SendTestNotificationsService } from '../notifications-bar/send-test-notifications.service';
import { Subject, Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { takeUntil } from 'rxjs/operators';
import { Role } from '../../role';
import { UserService } from '../../user.service';
import { FileUploadService } from '../edit-question-block/file-upload.service';
import { TranslateService } from '@ngx-translate/core';
import { MomentService } from '../../i18n/moment-locale.service';

@Component({
  selector: 'curr-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements OnInit, OnDestroy {
  notificationSubscription: any;
  switchState = 'minimized';
  isNotiMinimized = true;
  isNameDDOpen = false;
  isActive = false;
  name = '';
  private subscription$ = new Subscription();
  studentPages = [];
  generalPages = [];

  initializeMenuItems() {
    this.translateService.get([
      'header.menu.home',
      'header.menu.subject',
      'header.menu.recordings',
      'header.menu.myProfile',
      'header.menu.mySavedMaterials',
      'header.menu.orientationVideo',
      'header.menu.privacyPolicy',
      'header.menu.termsOfService'
    ]).subscribe(translations => {
      this.studentPages = [
        { icon: 'home', name: translations['header.menu.home'], rout: '/dashboard' },
        { icon: 'hat', name: translations['header.menu.subject'], rout: '/courses-details', disabled: false },
        // { icon: 'tv', name: translations['header.menu.recordings'], rout: '/dashboard' },
        { icon: 'avatar', name: translations['header.menu.myProfile'], rout: '/profile' },
        // { icon: 'save-bookmark', name: translations['header.menu.mySavedMaterials'], rout: '/subject-notes' },
        { icon: 'orientation-video', name: translations['header.menu.orientationVideo'], rout: '/orientation-video' },
        // { icon: 'referral-program', name: 'Referral Program', rout: '/referral' },
        // { icon: 'settings', name: 'Settings', rout: '/dashboard' }
      ];
      this.generalPages = [
        // { icon: 'lock', name: translations['header.menu.privacyPolicy'], rout: '/policy' },
        // { icon: 'assignment', name: translations['header.menu.termsOfService'], rout: '/tos' }
        // { icon: 'logout', name: 'Logout', rout: '/logout', isRed: true }
      ];
    });
  };
  logo = 'logo-c';
  profile;
  imagePreviewFile;
  isSafari: boolean;
  isExamPreparation: boolean = false;
  isLoadedSubscriptionInfo: boolean = false;
  isISGSE: boolean = false;

  // Language switcher options
  languages = [];
  selectedLanguage = 'en';
  now!: string;

  private destroy$: Subject<void> = new Subject<void>();

  constructor(
    private router: Router,
    private fus: FileUploadService,
    private stn: SendTestNotificationsService,
    private userService: UserService,
    private toastr: ToastrService,
    private translateService: TranslateService,
    private momentService: MomentService
  ) {
    this.now = this.momentService.format(new Date(), 'LLLL');
    this.notificationSubscription = this.stn.getNotificationSubscription();
  }

  ngOnInit(): void {
    // Initialize menu items with translations
    this.initializeMenuItems();

    this.userService.getCurrUserProfileInfo().pipe(takeUntil(this.destroy$)).subscribe(resp => {
      this.profile = resp;
      this.name = this.profile.first_name + ' ' + this.profile.last_name;

      // Update profile route after menu items are initialized
      if (this.studentPages.length > 3) {
        this.studentPages[3].rout = `/profile/${ this.profile.id }`;
      }

      // Set the selected language based on the profile language
      if (this.profile.language) {
        // Extract first two letters of language name as code (lowercase)
        this.selectedLanguage = this.profile.language.substring(0, 2).toLowerCase();
        this.translateService.use(this.selectedLanguage);
      } else {
        this.translateService.use('en');
      }

      if (this.profile.photo) {
        this.getPofileImageOnChange(this.profile.photo);
      }
    });
    this.userService.getUserLanguages().pipe(takeUntil(this.destroy$)).subscribe(res => {

      if (res.length <= 1) {
        this.languages = [];
        return;
      }
        // Map the language strings to objects with code and name properties
      this.languages = res.map(lang => {
        // Use 'ch' for Chinese language, otherwise extract first two letters of language name as code (lowercase)
        let code;
        if (lang === 'Chinese') {
          code = 'ch';
        } else {
          code = lang.substring(0, 2).toLowerCase();
        }
        return { code, name: lang };
      });
    });
    this.getLogo();
    this.subscription$.add(
      this.userService.activeUser$.subscribe(res => {
        if (res && res.photo) {
          this.getPofileImageOnChange(res.photo);
        }
      }));
    this.checkSafari();

    this.getUserSubscriptionInfo();

    this.userService.isISGSE().subscribe(isISGSE => {
      this.isISGSE = isISGSE;
      this.studentPages.find(page => page.name === 'Subject').disabled = isISGSE;
    })
  }

  private getUserSubscriptionInfo() {
    this.subscription$.add(
      this.userService.getSubscriptioninfo().subscribe(resp => {
        if (resp.length > 0) {
          this.isExamPreparation = resp[0].paymentPlanType === 'EXAM_PREPARATION';
        }
        this.isLoadedSubscriptionInfo = true;
      }));
  }

  getPofileImageOnChange(image) {
    this.subscription$.add(
      this.fus.download(image, 'IMAGE').subscribe((img: any) => {
        if (img.data.bytes) {
          this.imagePreviewFile = 'data:image/png;base64,' + img.data.bytes;
        }
      }));
  }

  goToDashboard(): void {
    this.router.navigateByUrl('/dashboard');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.subscription$.unsubscribe();
  }

  showNotificationBar(): void {
    this.isNotiMinimized = !this.isNotiMinimized;
    this.isNotiMinimized ? (this.switchState = 'minimized') : (this.switchState = 'opened');
  }

  userRole = Role;

  toggleStudentMenu(): void {
    this.isActive = !this.isActive;
  }

  menuClosing(): void {
    this.isActive = false;
  }

  menuClick(path: string): void {
    if (path === 'managing-api-keys') {
      this.router.navigate([`/managing-api-keys`]);
      return;
    }
    if (path === 'profile' || path === 'avatar') {
      this.router.navigate([`/profile/${ this.profile.id }`]);
      return;
    }
    if (path === 'video') {
      this.router.navigate([`/orientation-video`]);
      return;
    }
    // if (path === 'referral') {
    //   this.router.navigate([`/referral`]);
    //   return;
    // }
    // if (path === 'settings') {
    //   // navigate to settings
    // }
    // if (path === 'dashboard') {
    //   this.goToDashboard();
    //   // TODO: check if fix is working!!!
    //   if (this.profile.user_type === this.userRole.Teacher) {
    //     const ganttWithoutDataComponent = new GanttWithoutDataComponent(this.dialog);
    //     ganttWithoutDataComponent.importTimeTable();
    //   }
    // }
    // if (path === 'notes') {
    //   this.router.navigate(['/subjects/1/notes']);
    // }
    if (path === 'logout') {
      this.userService.saveUser({ role: Role.Unauthorized });
      window.localStorage.clear();
      this.router.navigateByUrl('');
      return;
    }
  }

  getLogo(): void {
    const url = window.location.href;
    if (url.includes('demo')) {
      this.logo = 'school-logo';
    }
    if (url.includes('c.curr')) {
      this.logo = 'logo-c';
    }
    if (url.includes('live.curr')) {
      this.logo = 'school-logo';
    }
    if (url.includes('test.curr')) {
      this.logo = 'school-logo';
    }
  }

  checkSafari() {
    const safariAgent = navigator.userAgent.toLowerCase().indexOf('safari/') > -1;
    const chromeAgent = navigator.userAgent.toLowerCase().indexOf('chrome/') > -1;
    if ((chromeAgent) && (safariAgent)) {
      this.isSafari = false;
    }
  }

  navigateToTutorSession(): void {
    // this.userService.getTutorSessionLinks()
    //   .pipe(
    //     first(),
    //     catchError(err => {
    //       if (err) {
    //         this.toastr.info('No tutor sessions yet.');
    //       }
    //       return EMPTY;
    //     }))
    //   .subscribe(res => {
    //     if (this.isSafari === false) {
    //       window.open(res.data, '_blank');
    //     } else {
    //       window.location.assign(res.data);
    //     }
    //   });
  }

  /** Change the language of the user profile
   * @param language - The full name of the language (e.g., 'English')
   * @param code - The two-letter code of the language (e.g., 'en')
   */
  changeLanguage(language: string, code: string): void {
    this.userService.updateUserLanguage(language)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.selectedLanguage = code;
        // Change the active language in the TranslateService
        this.translateService.use(code);

        this.momentService.setLocale(code);
        this.now = this.momentService.format(new Date(), 'LLLL');

        // Use the translateService to get the translated message
        this.translateService.get('header.languageUpdated').subscribe((res: string) => {
          this.toastr.success(res || 'Language updated successfully');
        });
      }, error => {
        this.translateService.get('header.languageUpdateFailed').subscribe((res: string) => {
          this.toastr.error(res || 'Failed to update language');
        });
      });
  }
}
